import { useCallback, useId, useState, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Swiper as SwiperType } from 'swiper';

import Carousel, { useCarousel } from '_components/Carousel';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackActivityNavClick } from '_utils/GA4/trackActivityNavigationGridEvent';

import ActivityCard from './ActivityCard';
import type { ActivityNavigationItem } from './types';
import styles from './ActivityNavigationGrid.module.scss';

export interface ActivityNavigationGridProps {
  /** Introduces the navigation set, e.g. "Explore by activity". */
  heading: string;
  /** The type of the navigation grid. */
  type: string;
  /**
   * Optional explainer line under the heading. Not part of the Figma
   * component — added at Alex's request for surfaces that need more context
   * than the heading alone gives.
   */
  body?: string;
  /** Curated list of 4–8 activities. */
  items: ActivityNavigationItem[];
  /**
   * Called before navigation on every card click, for host-specific side
   * effects. The GA4 `click_activity_nav` hit (ED-415) is sent by the grid
   * itself and does not need wiring up here: it reports the page path, so the
   * CIP and the CLP are already distinguishable without the host naming its
   * own surface. Calling `preventDefault` suppresses the grid's deferred
   * navigation, leaving the host to handle routing.
   */
  onItemClick?: (
    item: ActivityNavigationItem,
    index: number,
    event: MouseEvent<HTMLAnchorElement>
  ) => void;
}

/**
 * Cards that skip lazy loading. Desktop shows about four and a half at once,
 * but preloading every one of them is a lot of weight for a strip that sits
 * below the hero — three matches the other carousels in this codebase.
 */
const PRIORITY_CARD_COUNT = 3;

/** The curated range the design system documents for this component. */
const MIN_CARDS = 4;
const MAX_CARDS = 8;

// ─── Inner component (has access to Carousel context) ─────────────────────────

interface ActivityNavigationGridInnerProps extends ActivityNavigationGridProps {
  headingId: string;
}

function ActivityNavigationGridInner({
  heading,
  headingId,
  body,
  items,
  onItemClick,
  type,
}: ActivityNavigationGridInnerProps) {
  const { t } = useTranslation('common');
  const { asPath } = useRouter();
  const {
    state: { isScrollable },
  } = useCarousel();

  // Query string dropped so the same grid on the same page aggregates into one
  // row in reporting regardless of the filters the visitor arrived with.
  const interactionLocation = asPath.split('?')[0];

  const handleCardClick = useCallback(
    (
      item: ActivityNavigationItem,
      index: number,
      event: MouseEvent<HTMLAnchorElement>
    ) => {
      onItemClick?.(item, index, event);

      const data = {
        interactionLocation,
        heading,
        linkUrl: item.href,
        linkText: item.label,
        index,
        type,
      };

      // A modified click keeps this page alive, so the hit is not at risk and
      // navigation must not be hijacked.
      if (isModifiedClick(event)) {
        trackActivityNavClick({ data });
        return;
      }

      // The host may already have handled the click itself (e.g. a router
      // push); defer only when the anchor is still going to navigate.
      if (event.defaultPrevented) {
        trackActivityNavClick({ data });
        return;
      }

      event.preventDefault();
      trackActivityNavClick({
        data,
        eventCallback: () => {
          window.location.href = item.href;
        },
      });
    },
    [heading, interactionLocation, onItemClick]
  );

  // Empty until the strip actually moves: seeding it with slide 1 would make
  // every screen reader announce the carousel position on page load, before
  // the user has done anything.
  const [statusMessage, setStatusMessage] = useState('');

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      setStatusMessage(
        t('activityNavigationGrid.slideStatus', {
          current: swiper.activeIndex + 1,
          total: items.length,
        })
      );
    },
    [items.length, t]
  );

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headingGroup}>
          <h2 id={headingId} className={styles.heading}>
            {heading}
          </h2>
          {body && <p className={styles.body}>{body}</p>}
        </div>

        {/* Arrows are desktop-only in the design; mobile uses native touch
            scrolling. They are also dropped entirely when every card already
            fits, rather than shown in a permanently disabled state. */}
        {isScrollable && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('carousel.prev')}
              className={styles.navButton}
              iconSize={24}
            />
            <Carousel.NavNext
              aria-label={t('carousel.next')}
              className={styles.navButton}
              iconSize={24}
            />
          </div>
        )}
      </div>

      <div
        className={styles.track}
        role="group"
        aria-roledescription={t('activityNavigationGrid.carousel')}
        aria-label={heading}
      >
        <Carousel.Track
          className={styles.swiper}
          spaceBetween={2}
          onSlideChange={handleSlideChange}
        >
          {items.map((item, index) => (
            <Carousel.Slide
              key={`${item.label}-${item.href}`}
              className={styles.slide}
            >
              <ActivityCard
                {...item}
                priority={index < PRIORITY_CARD_COUNT}
                onClick={(event) => handleCardClick(item, index, event)}
              />
            </Carousel.Slide>
          ))}
        </Carousel.Track>
      </div>

      <p className={styles.status} role="status" aria-live="polite">
        {statusMessage}
      </p>
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * ED-219 — Activity Navigation Grid.
 *
 * A curated strip of image-based navigation cards that acts as a visual
 * gateway into top-level activity or product categories. Shared by the CIP and
 * the CLP, so it takes its content entirely through props and reports clicks
 * back through `onItemClick` instead of knowing about either page.
 *
 * The strip is a carousel at every breakpoint: arrows on desktop, native touch
 * scrolling on mobile. It renders as a `<nav>` landmark labelled by its own
 * heading, with the card list exposed as an ARIA carousel group.
 *
 * Authoring guidance: 4–8 cards, labels of one to three words. Counts outside
 * that range still render — the carousel copes — but warn in development.
 */
const ActivityNavigationGrid = ({
  heading,
  body,
  items,
  onItemClick,
  type,
}: ActivityNavigationGridProps) => {
  const headingId = useId();

  if (!heading || !items?.length) return null;

  if (
    process.env.NODE_ENV !== 'production' &&
    (items.length < MIN_CARDS || items.length > MAX_CARDS)
  ) {
    console.warn(
      `ActivityNavigationGrid: ${items.length} cards supplied; the design system specifies ${MIN_CARDS}-${MAX_CARDS}.`
    );
  }

  return (
    <nav
      className={styles.root}
      aria-labelledby={headingId}
      data-testid="activity-navigation-grid"
    >
      <div className={styles.inner}>
        <Carousel.Provider>
          <ActivityNavigationGridInner
            heading={heading}
            headingId={headingId}
            body={body}
            items={items}
            onItemClick={onItemClick}
            type={type}
          />
        </Carousel.Provider>
      </div>
    </nav>
  );
};

export default ActivityNavigationGrid;
